import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result, Alert } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { cssChunkErrorHandler } from '@/utils/cssChunkErrorHandler';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorType?: 'chunk' | 'css' | 'other';
}

class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // 检查是否是ChunkLoadError或CSS chunk加载错误
    if (error.name === 'ChunkLoadError' || 
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk') ||
        error.message.includes('vendors-async.css')) {
      
      // 判断错误类型
      let errorType: 'chunk' | 'css' | 'other' = 'other';
      if (error.message.includes('Loading CSS chunk')) {
        errorType = 'css';
      } else if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
        errorType = 'chunk';
      }
      
      return { hasError: true, error, errorType };
    }
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);
    
    // 如果是chunk加载错误，记录到错误处理器
    if (error.name === 'ChunkLoadError' || 
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk')) {
      console.log('Detected chunk loading error, attempting to handle...');
      
      // 如果是CSS chunk错误，尝试使用CSS错误处理器处理
      if (error.message.includes('Loading CSS chunk')) {
        // CSS错误处理器已经在全局监听，这里只需要记录
        console.log('CSS chunk error detected, CSS error handler will handle it');
      }
    }
  }

  handleReload = () => {
    // 清除缓存并重新加载页面
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    
    // 清除CSS错误处理器的重试计数
    cssChunkErrorHandler.clearRetryCounts();
    
    // 延迟重新加载，确保缓存清理完成
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  handleRetryCSS = () => {
    // 获取失败的CSS文件并尝试重新加载
    const failedFiles = cssChunkErrorHandler.getFailedFiles();
    if (failedFiles.length > 0) {
      console.log('尝试重新加载失败的CSS文件:', failedFiles);
      failedFiles.forEach(({ href }) => {
        cssChunkErrorHandler.retryCSSFile(href);
      });
      
      // 清除错误状态，允许组件重新渲染
      this.setState({ hasError: false, error: undefined, errorType: undefined });
    }
  };

  render() {
    if (this.state.hasError) {
      const { errorType } = this.state;
      
      let title = '页面加载失败';
      let subTitle = '检测到代码块加载错误，这通常是由于网络问题或缓存问题导致的。';
      let extra = [
        <Button
          key="reload"
          type="primary"
          icon={<ReloadOutlined />}
          onClick={this.handleReload}
        >
          重新加载页面
        </Button>,
        <Button
          key="back"
          onClick={() => window.history.back()}
        >
          返回上一页
        </Button>,
      ];

      // 根据错误类型显示不同的提示和操作
      if (errorType === 'css') {
        title = 'CSS样式加载失败';
        subTitle = '检测到CSS样式文件加载失败，这可能导致页面样式异常。';
        
        // 添加CSS重试按钮
        extra.unshift(
          <Button
            key="retry-css"
            icon={<ExclamationCircleOutlined />}
            onClick={this.handleRetryCSS}
          >
            重试加载样式
          </Button>
        );
      } else if (errorType === 'chunk') {
        title = '代码块加载失败';
        subTitle = '检测到JavaScript代码块加载失败，这可能导致页面功能异常。';
      }

      return (
        <Result
          status="error"
          title={title}
          subTitle={subTitle}
          extra={extra}
        >
          {errorType === 'css' && (
            <Alert
              message="CSS加载失败提示"
              description="如果重试加载样式仍然失败，建议刷新页面或检查网络连接。"
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;



